import { DataFarmType } from 'src/app/domainObjects/dataFarmType';
import { PreviewDataImportRequest } from 'src/app/messaging/previewImportDataRequest';
import { ConfigService } from 'src/app/services/config/config.service';
import { DataTransformConfigViewModel } from 'src/app/viewModels/dataTransformConfigViewModel';
import { TransformerViewModel } from '../../../transformer-list/transformer-list.component';
import { ColumnFilter } from 'src/app/domainObjects/columnFilter';
import { EventGroupNamerRuleViewModel } from './eventGroupNamerRuleViewModel';
import { TransformDetailViewModelBase } from '../../../dataTransformConfig/transformers/base/base-transformer-ui/transformDetailViewModelBase';
import { DataDictionaryService } from 'src/app/services/dataDictionary/data-dictionary.service';
import { SortInfo } from 'src/app/domainObjects/sortInfo';
import { ValidationInfo, RequiredFieldValidation } from 'src/app/objectModel/validationInfo';
import { IncludeInfo } from 'src/app/domainObjects/includeInfo';
import { TransformerParametersViewModel } from '../transformer-parameters/transformerParametersViewModel';
import { TransformType } from 'src/app/domainObjects/transformType';
import { EventGroupNamerTransform, EventGroupNamerTransformConfig } from 'src/app/domainObjects/transformers/eventGroupNamerTransform';
import { EventGroupNamerPreviewDataResponse } from 'src/app/messaging/taskNamerPreviewDataResponse';

export class EventGroupNamerViewModel extends TransformDetailViewModelBase<EventGroupNamerTransform> {
    farmType: DataFarmType;
    sourceColumn: string;
    targetColumn: string;
    userColumn: string;
    timeColumn: string;
    runAll: boolean;
    deleteMisses = false;
    missName: string;
    
    groupBy: string;
    defTimeout: number;

    bufferRows: number;
    threads: number;
    searchWildCard: string;
    orderField: string;
    isOpenField: string;
    isCloseField: string;
    multiTargetColumn: boolean;
    spaceSeparator: boolean;
    removeRedundant: boolean;

    rules: Array<EventGroupNamerRuleViewModel> = [];

    parameters:TransformerParametersViewModel;
    // Preview options
    rows: number;
    sampleStart: number;
    sampleEnd: number;
    // Stats
    groupHitCount?: number;
    hitCount?: number;
    missCount?: number;
    hitRatio?: number;
    hitRatioDisplay?: string;
  
    request: PreviewDataImportRequest;
    response: EventGroupNamerPreviewDataResponse;
  
    constructor(
      dataDictService: DataDictionaryService,
      private configService: ConfigService,
      configViewModel: DataTransformConfigViewModel,
      transformViewModel: TransformerViewModel) {

        super(dataDictService, configViewModel, transformViewModel);

        const transformConfig = this.transform.transformConfig;
        this.parameters=new TransformerParametersViewModel();
        if (transformConfig) {
          this.farmType = transformConfig.farmType ?? transformViewModel.transform.dataFarmType;
          this.sourceColumn = transformConfig.defSourceField;
          this.userColumn = transformConfig.userField;
          this.targetColumn = transformConfig.defTargetField;
          this.deleteMisses = transformConfig.delMisses;
          this.missName = transformConfig.missesNamed;
          this.sampleEnd = transformConfig.sampleEnd + 1;
          this.sampleStart = transformConfig.sampleStart + 1;
          this.timeColumn = transformConfig.timestampField;
          this.runAll = transformConfig.runAll;
          this.sortInfo = transformConfig.sortInfo;
          if(transformConfig.includeInResults){
            this.includeInResults = transformConfig.includeInResults.map(item =>new IncludeInfo(item));
            this.parameters.includeInResults=this.includeInResults;
          }
          this.defTimeout = transformConfig.defTimeout;
          //parameters
          this.parameters.sourceColumn= transformConfig.defSourceField;
          this.parameters.targetColumn= transformConfig.defTargetField;
          this.parameters.userColumn= transformConfig.userField;
          this.parameters.timeColumn= transformConfig.timestampField;
          this.parameters.sortInfo= transformConfig.sortInfo;
          //this.parameters.includeInResults= transformConfig.in;
          this.parameters.sampleStart = transformConfig.sampleStart+ 1;
          this.parameters.sampleEnd = transformConfig.sampleEnd+ 1;
          this.parameters.runAll= transformConfig.runAll;
          this.parameters.deleteMisses = transformConfig.delMisses;
          this.parameters.missName= transformConfig.missesNamed;
          // advanced settings
          this.bufferRows = transformConfig.bufferRows;
          this.threads = transformConfig.threads;
          this.searchWildCard = transformConfig.searchWildCard;
          this.multiTargetColumn = transformConfig.multiColumnTarget;
          this.spaceSeparator = transformConfig.spaceSeparator;
          this.removeRedundant = transformConfig.removeRedundant;
          this.groupBy = transformConfig.groupBy;
          this.orderField = transformConfig.orderField;
          this.isOpenField = transformConfig.isOpenField;
          this.isCloseField = transformConfig.isCloseField;

          if (transformConfig.rules) {
            this.rules = transformConfig.rules.map(r => new EventGroupNamerRuleViewModel(r));
          }

          // default
          if (!this.timeColumn || this.timeColumn?.length === 0) {
            this.timeColumn = this.getDefaultTimestampColumn();
          }
        }
    }

    override configureValidationInfo(): ValidationInfo[] {
      const infos = [];

      infos.push( new RequiredFieldValidation('groupByField', 'Group By'));
      infos.push( new RequiredFieldValidation('userField', 'User Field'));
      infos.push( new RequiredFieldValidation('timestampField', 'Timestamp Field'));
      
      return infos;
    }

    reindexRules(): void {
      for (let i = 0; i < this.rules.length; i++) {
        this.rules[i].index = i + 1;
      }
      this.rules.sort((a, b) => a.index - b.index);
    }

    public override onTransformRunCompleted(response: EventGroupNamerPreviewDataResponse): void {
      // A cache-hit run carries no stats at all (they're deliberately stripped
      // before a CachedProcessingResult is persisted -- see
      // project_stats_vs_fullrun_separation memory). Nothing has changed since
      // whatever last populated the stats currently on screen, so they're still
      // accurate -- leave them exactly as they are instead of wiping them blank.
      if (
        !response?.previewStats ||
        !response?.totalStats ||
        !response.totalStats.globalStat ||
        !response.totalStats.ruleStats ||
        !response.previewStats.globalStat ||
        !response.previewStats.ruleStats
      ) {
        return;
      }

      // Clear all stats prior to rebinding new values
      this.groupHitCount = null;
      this.hitCount = null;
      this.hitRatio = null;
      this.missCount = null;
      this.hitRatioDisplay ="0.00%";
      this.rows = 0;
      this.rules.forEach( r => {
        r.groupHits = null;
        r.rowHits = null;
        r.sampleGroupHits = null;
        r.sampleRowHits = null;
      });

      if (this.parameters.runAll) {
        // Map Transform Global and Rule stats
        this.groupHitCount = response.totalStats?.globalStat.groupHits;
        this.hitCount = response.totalStats?.globalStat.rowHits;

        if (response.totalStats?.globalStat.hitRate) {
          this.hitRatio = response.totalStats.globalStat.hitRate;
          this.hitRatioDisplay = `${(this.hitRatio * 100).toFixed(2)}%`;
        }
        this.missCount = response.totalStats?.globalStat.rowMisses;
      } else {
        // Map Transform Global and Rule stats
        this.groupHitCount = response.previewStats?.globalStat.groupHits;
        this.hitCount = response.previewStats.globalStat.rowHits;

        if (response.previewStats.globalStat.hitRate) {
          this.hitRatio = response.previewStats.globalStat.hitRate;
          this.hitRatioDisplay = `${(this.hitRatio * 100).toFixed(2)}%`;
        }
        this.missCount = response.previewStats.globalStat.rowMisses;
      }
      
      this.rows = this.parameters.runAll ? response.currentData?.totalRows : response.activeData?.totalRows;
      // map the rule stats to the rule
      if (response.previewStats.ruleStats) {
        for (let i = 0; i < this.rules.length; i++) {
          const ruleStat = response.previewStats.ruleStats.find(r => r.ruleIndex === i);
          this.rules[i].sampleGroupHits = ruleStat?.groupHits;
          this.rules[i].sampleRowHits = ruleStat?.rowHits;
        }
      }
      // map the rule stats to the rule
      if (response.totalStats.ruleStats) {
        for (let i = 0; i < this.rules.length; i++) {
          const ruleStat = response.totalStats.ruleStats.find(r => r.ruleIndex === i);
          this.rules[i].groupHits = ruleStat?.groupHits;
          this.rules[i].rowHits = ruleStat?.rowHits;
        }
      }
    }

    override toTransformConfig(): EventGroupNamerTransformConfig {
      const config = new EventGroupNamerTransformConfig();
      config.farmType = this.farmType;
      config.transformType = TransformType.eventGroupNamer;
      config.defSourceField=this.parameters.sourceColumn;
      config.defTargetField=this.parameters.targetColumn;
      config.userField=this.parameters.userColumn;
      config.timestampField=this.parameters.timeColumn;
      if(this.parameters.sortInfo){
        if(this.parameters.sortInfo.length==0){
          config.sortInfo =undefined;
        }else{
          config.sortInfo=this.parameters.sortInfo;
        }
      }
      if(this.parameters.includeInResults){
        if(this.parameters.includeInResults.length==0){
          config.includeInResults =undefined;
        }else{
          config.includeInResults =this.parameters.includeInResults.map(item => item.includeField);
        }
      }
      config.sampleStart =this.parameters.sampleStart-1;
      config.sampleEnd =this.parameters.sampleEnd-1;
      config.runAll=this.parameters.runAll;
      config.delMisses=this.parameters.deleteMisses;
      config.missesNamed=this.parameters.missName;

      config.groupBy = this.groupBy;
      config.defTimeout = this.defTimeout;

      config.bufferRows = this.bufferRows;
      config.threads = this.threads;
      config.searchWildCard = this.searchWildCard;
      config.orderField = this.orderField;
      config.isOpenField = this.isOpenField;
      config.isCloseField = this.isCloseField;
      config.multiColumnTarget = this.multiTargetColumn;
      config.spaceSeparator = this.spaceSeparator;
      config.removeRedundant = this.removeRedundant;

      config.rules = this.rules.map(r => {
        const rule = r.toEventGroupNamerRule();
        rule.targetField = config.defTargetField;

        return rule;
      });

      return config;
    }
  }
